/**
 * useSessionTimeout Hook
 * HIPAA Compliance: Automatic session timeout after period of inactivity
 * Regulation: 45 CFR 164.312(a)(2)(iii) - Automatic Logoff
 */

import { useEffect, useCallback, useRef, useState } from 'react';

// Default timeout: 15 minutes (configurable via env)
const SESSION_TIMEOUT_MINUTES = parseInt(process.env.REACT_APP_SESSION_TIMEOUT || '15', 10);
const SESSION_TIMEOUT = SESSION_TIMEOUT_MINUTES * 60 * 1000;
const WARNING_BEFORE = 2 * 60 * 1000; // Show warning 2 minutes before timeout

/**
 * Hook that tracks user activity and triggers timeout/warning callbacks
 * @param {Object} options
 * @param {Function} options.onTimeout - Called when session times out
 * @param {Function} options.onWarning - Called when warning should be shown
 * @param {boolean} options.enabled - Whether timeout tracking is enabled
 * @returns {Object} - { resetTimer, remainingTime, isWarningVisible }
 */
export const useSessionTimeout = ({ onTimeout, onWarning, enabled = true }) => {
  const timeoutRef = useRef(null);
  const warningRef = useRef(null);
  const [remainingTime, setRemainingTime] = useState(SESSION_TIMEOUT);
  const [isWarningVisible, setIsWarningVisible] = useState(false);
  const countdownRef = useRef(null);

  const clearAllTimers = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (warningRef.current) {
      clearTimeout(warningRef.current);
      warningRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  const resetTimer = useCallback(() => {
    if (!enabled) return;

    clearAllTimers();
    setIsWarningVisible(false);
    setRemainingTime(SESSION_TIMEOUT);

    // Set warning timer
    warningRef.current = setTimeout(() => {
      setIsWarningVisible(true);
      onWarning?.();

      // Start countdown
      let timeLeft = WARNING_BEFORE;
      setRemainingTime(timeLeft);

      countdownRef.current = setInterval(() => {
        timeLeft -= 1000;
        setRemainingTime(timeLeft);

        if (timeLeft <= 0) {
          clearInterval(countdownRef.current);
        }
      }, 1000);
    }, SESSION_TIMEOUT - WARNING_BEFORE);

    // Set timeout timer
    timeoutRef.current = setTimeout(() => {
      clearAllTimers();
      setIsWarningVisible(false);
      onTimeout();
    }, SESSION_TIMEOUT);
  }, [enabled, onTimeout, onWarning, clearAllTimers]);

  const extendSession = useCallback(() => {
    resetTimer();
  }, [resetTimer]);

  useEffect(() => {
    if (!enabled) {
      clearAllTimers();
      return;
    }

    // Events that indicate user activity
    const activityEvents = [
      'mousedown',
      'keydown',
      'scroll',
      'touchstart',
      'mousemove',
      'click'
    ];

    // Throttle to avoid excessive timer resets
    let lastActivity = Date.now();
    const throttleMs = 1000; // Only reset timer once per second max

    const handleActivity = () => {
      const now = Date.now();
      if (now - lastActivity > throttleMs) {
        lastActivity = now;
        // Only reset if warning is not visible
        if (!isWarningVisible) {
          resetTimer();
        }
      }
    };

    // Add event listeners
    activityEvents.forEach(event =>
      document.addEventListener(event, handleActivity, { passive: true })
    );

    // Start initial timer
    resetTimer();

    // Cleanup
    return () => {
      activityEvents.forEach(event =>
        document.removeEventListener(event, handleActivity)
      );
      clearAllTimers();
    };
  }, [enabled, resetTimer, clearAllTimers, isWarningVisible]);

  return {
    resetTimer,
    extendSession,
    remainingTime,
    isWarningVisible,
    remainingSeconds: Math.ceil(remainingTime / 1000)
  };
};

export default useSessionTimeout;
